import React from 'react';
import { Button, Select, Text, makeStyles, tokens } from '@fluentui/react-components';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  ChevronDoubleLeftRegular,
  ChevronDoubleRightRegular,
} from '@fluentui/react-icons';

interface PaginationBarProps {
  totalItems: number;
  startIndex: number;
  endIndex: number;
  currentPage: number;
  totalPages: number;
  pageOptions: number[];
  onGoToPage: (page: number) => void;
  /** Optional selection count suffix shown next to the item range. */
  selectedCount?: number;
}

const usePaginationBarStyles = makeStyles({
  bar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
});

export const PaginationBar: React.FC<PaginationBarProps> = ({
  totalItems,
  startIndex,
  endIndex,
  currentPage,
  totalPages,
  pageOptions,
  onGoToPage,
  selectedCount,
}) => {
  const styles = usePaginationBarStyles();

  return (
    <div className={styles.bar}>
      <Text size={200}>
        Showing {totalItems === 0 ? 0 : startIndex + 1} – {endIndex} of {totalItems}
        {selectedCount != null && selectedCount > 0 && ` (${selectedCount} selected)`}
      </Text>

      {totalPages > 1 && (
        <div className={styles.controls}>
          <Button
            appearance="subtle"
            icon={<ChevronDoubleLeftRegular />}
            disabled={currentPage <= 1}
            onClick={() => onGoToPage(1)}
            size="small"
            title="First page"
          />
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={currentPage <= 1}
            onClick={() => onGoToPage(currentPage - 1)}
            size="small"
            title="Previous page"
          />
          <Text size={200} style={{ whiteSpace: 'nowrap' }}>Page</Text>
          <Select
            value={String(currentPage)}
            onChange={(_e, data) => onGoToPage(Number(data.value))}
            size="small"
            aria-label="Go to page"
            style={{ minWidth: 'auto', width: 'auto' }}
          >
            {pageOptions.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Select>
          <Text size={200} style={{ whiteSpace: 'nowrap' }}>of {totalPages}</Text>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={currentPage >= totalPages}
            onClick={() => onGoToPage(currentPage + 1)}
            size="small"
            title="Next page"
          />
          <Button
            appearance="subtle"
            icon={<ChevronDoubleRightRegular />}
            disabled={currentPage >= totalPages}
            onClick={() => onGoToPage(totalPages)}
            size="small"
            title="Last page"
          />
        </div>
      )}
    </div>
  );
};
